import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Clock, GraduationCap, CheckCircle2, ArrowLeft } from "lucide-react";
import PageHero from "../components/PageHero";
import api from "../lib/api";
import { useApplyModal } from "../lib/applyModal";
import Gallery from "../components/Gallery";
import SEOHead from "../components/SEOHead";

const COURSE_GALLERY = [
  "https://images.pexels.com/photos/37561815/pexels-photo-37561815.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.pexels.com/photos/7480243/pexels-photo-7480243.jpeg?auto=compress&cs=tinysrgb&w=800",
  "https://images.unsplash.com/photo-1738162837329-2f2a2cdebb5e?w=800&q=80",
  "https://images.unsplash.com/photo-1726220230389-cd363f6a52eb?w=800&q=80",
  "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80",
  "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=800&q=80",
];

const categoryLabels = {
  long_term: "Long Term Courses",
  short_term: "Short Term Courses",
  corporate: "Corporate Training",
  earn_while_learn: "Earn While You Learn",
};

export default function CoursesPage() {
  const { category } = useParams();
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState(category || "all");

  useEffect(() => {
    api.get("/courses", { params: { active_only: true } }).then((r) => setCourses(r.data));
  }, []);

  useEffect(() => {
    if (category) {
      const key = category.replace(/-/g, "_");
      setFilter(key);
    } else {
      setFilter("all");
    }
  }, [category]);

  const filtered = filter === "all" ? courses : courses.filter((c) => c.category === filter);

  const tabs = [
    { key: "all", label: "All Courses" },
    { key: "long_term", label: "Long Term" },
    { key: "short_term", label: "Short Term" },
    { key: "corporate", label: "Corporate" },
    { key: "earn_while_learn", label: "Earn While Learn" },
  ];

  return (
    <>
      <PageHero
        kicker="Our Courses"
        title={categoryLabels[filter] || "Explore Our Technical Courses"}
        subtitle="German-certified curriculum, hands-on labs and strong placement support across all our programs."
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`px-4 py-2 text-sm font-semibold rounded-full transition border ${
                  filter === t.key
                    ? "bg-[#0B2A6B] text-white border-[#0B2A6B]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#0B2A6B] hover:text-[#0B2A6B]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((c, i) => {
              const Icon = Icons[c.icon] || Icons.GraduationCap;
              return (
                <Link
                  key={c.id}
                  to={`/courses/detail/${c.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 tilt-card animate-fade-up"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={c.image_url} alt={c.title} className="absolute inset-0 w-full h-full object-cover zoom-img" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[11px] font-bold uppercase">
                      {c.category.replace("_", " ")}
                    </span>
                    <div className="absolute bottom-4 left-4 flex items-center gap-1 text-white text-xs font-medium">
                      <Clock className="h-3.5 w-3.5" />
                      {c.duration}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="h-10 w-10 rounded-lg bg-yellow-400/15 group-hover:bg-yellow-400 transition flex items-center justify-center">
                        <Icon className="h-5 w-5 text-yellow-600 group-hover:text-[#0B2A6B] transition" />
                      </div>
                      <h3 className="font-bold text-[#0B2A6B] text-lg leading-snug group-hover:text-yellow-600 transition">
                        {c.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-3">{c.description}</p>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs text-slate-500">{c.eligibility}</span>
                      <span className="text-yellow-600 font-bold text-sm inline-flex items-center gap-1 group-hover:translate-x-1 transition">
                        Details <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-slate-500">No courses available in this category.</div>
          )}
        </div>
      </section>
    </>
  );
}

export function CourseDetailPage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const { openApply } = useApplyModal();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/courses/slug/${slug}`)
      .then((r) => setCourse(r.data))
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center">Loading...</div>;
  if (!course) return <div className="py-32 text-center">Course not found.</div>;

  const Icon = Icons[course.icon] || GraduationCap;

  return (
    <>
      <PageHero kicker={course.category.replace("_", " ")} title={course.title} subtitle={course.description} />

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/courses" className="inline-flex items-center gap-1 text-[#0B2A6B] font-semibold hover:text-yellow-600 mb-8">
            <ArrowLeft className="h-4 w-4" /> Back to all courses
          </Link>

          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
                <img src={course.image_url} alt={course.title} className="w-full h-80 object-cover" />
              </div>
              <h2 className="text-2xl font-bold text-[#0B2A6B] mb-4">About this course</h2>
              <SEOHead
                title={`${course.title} | Kiki Centre for Technology`}
                description={course.description}
                image={course.image_url}
                jsonLd={[
                  {
                    "@context": "https://schema.org",
                    "@type": "Course",
                    name: course.title,
                    description: course.description,
                    provider: {
                      "@type": "EducationalOrganization",
                      name: "Kiki Centre for Technology",
                      sameAs: "https://kikicentre.com",
                    },
                  },
                  {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      { "@type": "ListItem", position: 1, name: "Home", item: "https://kikicentre.com/" },
                      { "@type": "ListItem", position: 2, name: "Courses", item: "https://kikicentre.com/courses" },
                      { "@type": "ListItem", position: 3, name: course.title },
                    ],
                  },
                ]}
              />
              <p className="text-slate-700 leading-relaxed mb-8">{course.description}</p>

              <h3 className="text-xl font-bold text-[#0B2A6B] mb-4">Workshop Gallery</h3>
              <div className="mb-10">
                <Gallery
                  images={course.gallery && course.gallery.length ? course.gallery : COURSE_GALLERY}
                  testid="course-gallery"
                />
              </div>

              <h3 className="text-xl font-bold text-[#0B2A6B] mb-4">Key Learning Areas</h3>
              <ul className="grid sm:grid-cols-2 gap-3 mb-10">
                {course.highlights?.map((h) => (
                  <li key={h} className="flex items-start gap-3 p-4 rounded-xl bg-yellow-50 border border-yellow-100">
                    <CheckCircle2 className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
                    <span className="font-medium text-slate-700">{h}</span>
                  </li>
                ))}
              </ul>

              {course.syllabus?.length > 0 && (
                <>
                  <h3 className="text-xl font-bold text-[#0B2A6B] mb-4">Course Syllabus</h3>
                  <div className="space-y-4 mb-10">
                    {course.syllabus.map((g) => (
                      <div key={g.group} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="syllabus-group">
                        <h4 className="font-bold text-[#0B2A6B] mb-3">{g.group}</h4>
                        <div className="flex flex-wrap gap-2">
                          {g.items.map((it) => (
                            <span key={it} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">{it}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {course.careers?.length > 0 && (
                <>
                  <h3 className="text-xl font-bold text-[#0B2A6B] mb-4">Career Prospects</h3>
                  <div className="flex flex-wrap gap-2 mb-10">
                    {course.careers.map((c) => (
                      <span key={c} className="px-4 py-1.5 rounded-full bg-yellow-50 border border-yellow-100 text-[#0B2A6B] text-sm font-semibold">{c}</span>
                    ))}
                  </div>
                </>
              )}

              <h3 className="text-xl font-bold text-[#0B2A6B] mb-4">Certification & Placement</h3>
              <div className="grid sm:grid-cols-3 gap-4 mb-10">
                {[
                  ["German Certified", "Assessment & certification aligned with German Chamber of Skilled Crafts (HWK), Koblenz standards."],
                  ["Govt. of India", "Recognised through CGSC / ASDC under the Ministry of Skill Development & Entrepreneurship."],
                  ["95%+ Placement", "Strong placement support with 60+ hiring partners including Maruti, Denso, Stryker & Boston Scientific."],
                ].map(([t, d]) => (
                  <div key={t} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
                    <h4 className="font-bold text-[#0B2A6B] text-sm mb-1">{t}</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>

              <h3 className="text-xl font-bold text-[#0B2A6B] mb-4">Who should apply</h3>
              <div className="rounded-xl bg-[#0B2A6B] text-white p-6">
                <p className="text-sm text-slate-200 leading-relaxed">
                  This programme is ideal for <span className="text-yellow-300 font-semibold">{course.eligibility}</span> who want a
                  hands-on, industry-ready career in advanced manufacturing and technology. Training is delivered on world-class
                  German, Japanese and Indian machinery by expert faculty.
                </p>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="bg-[#0B2A6B] p-6 text-white">
                  <div className="h-14 w-14 rounded-xl bg-yellow-400 flex items-center justify-center mb-3">
                    <Icon className="h-7 w-7 text-[#0B2A6B]" />
                  </div>
                  <h3 className="text-xl font-bold">{course.title}</h3>
                </div>
                <div className="p-6 space-y-4">
                  <Row label="Category" value={course.category.replace("_", " ")} />
                  <Row label="Duration" value={course.duration} />
                  <Row label="Eligibility" value={course.eligibility} />
                  {course.salary && <Row label="Est. Salary" value={course.salary} />}
                  {course.age && <Row label="Age" value={course.age} />}
                  <button
                    onClick={() => openApply(course.title)}
                    data-testid="course-apply-btn"
                    className="block text-center w-full px-6 py-3 rounded-lg btn-primary mt-2"
                  >
                    Apply for this Course
                  </button>
                  <Link
                    to="/contact"
                    className="block text-center w-full px-6 py-3 rounded-lg border-2 border-[#0B2A6B] text-[#0B2A6B] font-semibold hover:bg-[#0B2A6B] hover:text-white transition"
                  >
                    Enquire Now
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500 font-medium">{label}</span>
      <span className="text-[#0B2A6B] font-semibold capitalize">{value || "—"}</span>
    </div>
  );
}
